import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { formatPrice } from '../utils/priceFormatter';
import { getAssetDescription } from '../utils/assetDescriptions';
import { useTheme } from '../contexts/ThemeContext';
import { usePositionContext } from '../contexts/PositionContext';

interface LivePriceProps {
  selectedSymbol: string;
  currentPrice: string;
  priceChange: string;
  isPricePositive: boolean;
  priceColor: string;
  isRightPanelVisible?: boolean;
}


const LivePrice: React.FC<LivePriceProps> = ({
  selectedSymbol,
  currentPrice,
  priceChange,
  isPricePositive,
  priceColor,
  isRightPanelVisible = false,
}) => {
  const { theme } = useTheme();
  const { openPositions } = usePositionContext();
  
  // Calculate P&L for current symbol
  const calculateSymbolPnL = () => {
    const symbolPositions = openPositions.filter(pos => pos.symbol === selectedSymbol);
    if (symbolPositions.length === 0) return { totalPnL: 0, totalPnLPercent: 0 };
    
    let totalPnL = 0;
    let totalPnLPercent = 0;
    
    symbolPositions.forEach(position => {
      const entryPrice = parseFloat(position.entry);
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      const size = parseFloat(position.size);
      
      let pnl: number;
      if (position.type === 'Buy') {
        pnl = (currentPriceNum - entryPrice) * size;
      } else {
        pnl = (entryPrice - currentPriceNum) * size;
      }
      
      totalPnL += pnl;
      
      // Calculate percentage based on position value
      const positionValue = entryPrice * size;
      if (positionValue > 0) {
        totalPnLPercent += (pnl / positionValue) * 100;
      }
    });
    
    return { 
      totalPnL: Math.round(totalPnL * 100) / 100, 
      totalPnLPercent: Math.round(totalPnLPercent * 100) / 100 
    };
  };
  
  const { totalPnL, totalPnLPercent } = calculateSymbolPnL();
  const hasPositions = openPositions.some(pos => pos.symbol === selectedSymbol);
  return (
    <div className="py-0 ">
          {/* <div 
      className="absolute bottom-12 left-0 p-2"
      style={{
        width: isRightPanelVisible ? 'calc(100% - 15px)' : '100%',
        marginRight: isRightPanelVisible ? '15px' : '0'
      }}
    ></div> */}
      <div className={`theme-bg-secondary bg-opacity-95 p-3 py-4 rounded-lg z-10 shadow-xs bg-white border border-gray-200 ${theme === 'light' ? 'border-gray-200' : 'border-gray-800'}`}>
        <div className="flex flex-col items-center justify-center space-y-1 text-center">
          {/* Symbol + Price Label */}
          <div className="flex items-center justify-center">
            <h2 className="text-xs font-medium theme-text-white">
              {selectedSymbol}
            </h2>
          </div>
          
          {/* Live Price with Percentage on the right */}
          <div className="flex items-center justify-center space-x-3">
            {/* Price on the left */}
            <div className="flex items-baseline relative">
              <span className="live-price-text text-1xl font-bold theme-text-primary transition-all duration-300">
                <span
                  className={priceColor}
                  dangerouslySetInnerHTML={{ 
                    __html: formatPrice(parseFloat(currentPrice), selectedSymbol).replace(
                      /\$(\d+)/g, 
                      (match, digits) => `${parseInt(digits).toLocaleString()}`
                    )
                  }}
                />
              </span>              
            </div>
            
            {/* Percentage on the right */}
            <div className="flex items-center">
              <span className={`text-sm font-medium flex items-center ${isPricePositive ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                {isPricePositive ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 mr-1" />
                )}
                {priceChange && priceChange !== 'NaN%' ? priceChange : '+0.00%'}
              </span>
            </div>
          </div>
          
          {/* P&L Section - Only show if there are positions for this symbol */}
          {hasPositions && (
            <div className="flex items-center justify-center space-x-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs font-medium theme-text-quaternary">Current Positions PnL:</span>
              <div className="flex items-center space-x-1">
                <span className={`text-sm font-medium ${totalPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                  {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
                </span>
                <span className={`text-xs font-medium ${totalPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                  ({totalPnL >= 0 ? '+' : ''}{totalPnLPercent.toFixed(2)}%)
                </span>
              </div>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default LivePrice;
